import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Default to dark theme
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('portfolio-theme');
    return savedTheme || 'dark';
  });

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('portfolio-theme', theme);
    
    // Apply theme-specific CSS variables - Light theme is soft daylight theme
    if (theme === 'light') {
      // Soft daylight theme - warm, pleasant colors that are easy on the eyes
      document.documentElement.style.setProperty('--bg-color', '#fafafa');
      document.documentElement.style.setProperty('--text-color', '#2c3e50');
      document.documentElement.style.setProperty('--section-background-color', 'linear-gradient(to bottom left, rgba(250, 250, 252, 0.95), rgba(248, 249, 252, 0.98))');
      document.documentElement.style.setProperty('--image-gradient', 'linear-gradient(to bottom left, rgba(250, 250, 252, 0.9), rgba(248, 249, 252, 0.95))');
      document.documentElement.style.setProperty('--section-bg', 'linear-gradient(to bottom left, rgba(250, 250, 252, 0.95), rgba(248, 249, 252, 0.98))');
      document.documentElement.style.setProperty('--navbar-bg', 'rgba(255, 255, 255, 0.95)');
      document.documentElement.style.setProperty('--navbar-text', '#34495e');
      document.documentElement.style.setProperty('--card-bg', 'rgba(255, 255, 255, 0.85)');
      document.documentElement.style.setProperty('--card-border', 'rgba(200, 200, 210, 0.3)');
      document.documentElement.style.setProperty('--scrollbar-track', '#f0f0f0');
      document.body.style.backgroundColor = '#fafafa';
      document.body.style.color = '#2c3e50';
    } else {
      // Dark theme (original)
      document.documentElement.style.setProperty('--bg-color', '#000000');
      document.documentElement.style.setProperty('--text-color', '#ffffff');
      document.documentElement.style.setProperty('--section-background-color', 'linear-gradient(to bottom left, rgba(17, 16, 16, 0.582), rgba(12, 8, 24, 0.904))');
      document.documentElement.style.setProperty('--image-gradient', 'linear-gradient(to bottom left, rgba(17, 16, 16, 0.678), rgba(12, 10, 22, 0.863))');
      document.documentElement.style.setProperty('--section-bg', 'linear-gradient(to bottom left, rgba(17, 16, 16, 0.582), rgba(12, 8, 24, 0.904))');
      document.documentElement.style.setProperty('--navbar-bg', '#000000a9');
      document.documentElement.style.setProperty('--navbar-text', '#ffffff');
      document.documentElement.style.setProperty('--card-bg', 'rgba(0, 0, 0, 0.3)');
      document.documentElement.style.setProperty('--card-border', 'rgba(255, 255, 255, 0.1)');
      document.documentElement.style.setProperty('--scrollbar-track', '#000000');
      document.body.style.backgroundColor = '#0a0814';
      document.body.style.color = '#ffffff';
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

